<div class="card shadow-sm mb-4">
  <div class="card-body p-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
      <div>
        <h5 class="mb-1 fw-bold">Roles</h5>
        <span class="sub-text">System level access roles assigned to every user</span>
      </div>
      <button class="btn btn-primary d-flex align-items-center gap-2" (click)="onAddRole()">
        <i class="bi bi-plus-lg"></i>
        Add Role
      </button>
    </div>

    @if (isDataLoading) {
      <app-loading-spinner></app-loading-spinner>
    } @else {
      @if (roles.length === 0) {
        <app-info-alert>No roles found. Add your first role to get started.</app-info-alert>
      } @else {
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="bg-light">
              <tr>
                <th class="border-0 text-uppercase small fw-bold text-muted py-3">Role</th>
                <th class="border-0 text-uppercase small fw-bold text-muted py-3">Code</th>
                <th class="border-0 text-uppercase small fw-bold text-muted py-3">Description</th>
                <th class="border-0 text-uppercase small fw-bold text-muted py-3">Status</th>
                <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (role of roles; track role.id) {
                <tr>
                  <td class="py-3">
                    <div class="fw-semibold">{{ role.name }}</div>
                  </td>
                  <td class="py-3">
                    <span class="badge bg-secondary fw-semibold">{{ role.code }}</span>
                  </td>
                  <td class="py-3 small text-muted">{{ role.description || '—' }}</td>
                  <td class="py-3">
                    @if (role.is_active) {
                      <span class="badge bg-success-subtle text-success">Active</span>
                    } @else {
                      <span class="badge bg-light text-muted border">Retired</span>
                    }
                  </td>
                  <td class="py-3 text-end">
                    <div class="d-flex gap-2 justify-content-end">
                      <button class="btn btn-action-edit btn-sm" (click)="onEditRole(role)" title="Edit role">
                        <i class="bi bi-pencil-square"></i>
                      </button>
                      @if (role.is_active) {
                        <button class="btn btn-action-delete btn-sm" (click)="onRetireRole(role)" title="Retire role">
                          <i class="bi bi-archive"></i>
                        </button>
                      } @else {
                        <button class="btn btn-action-edit btn-sm" (click)="onRestoreRole(role)" title="Restore role">
                          <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
  </div>
</div>

<ng-template #roleModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <form [formGroup]="roleForm" (ngSubmit)="onSubmit()">
      <div class="modal-header py-3">
        <h5 class="modal-title">{{ isEditMode ? 'Edit Role' : 'Add Role' }}</h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>

      <div class="modal-body p-4">
        @if (formError) {
          <app-error-alert [error]="formError" />
        }

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">
            Name
            <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control" formControlName="name" placeholder="e.g. Sales Officer" />
          <div class="form-text text-muted">Shown in the UI. Safe to rename later.</div>
          @if (isInvalid('name')) {
            <div class="text-danger small mt-1">Name is required.</div>
          }
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">
            Code
            <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control" formControlName="code" placeholder="e.g. sales_officer" />
          @if (isEditMode) {
            <div class="form-text text-muted">Code cannot be changed - users reference roles by it.</div>
          } @else {
            <div class="form-text text-muted">
              Lowercase letters, digits and underscores, starting with a letter — the name in lower case with spaces
              replaced by underscores. For example
              <strong>Sales Officer</strong>
              becomes
              <code>sales_officer</code>
              . Permanent once created.
            </div>
            @if (suggestedCode && roleForm.get('code')?.value !== suggestedCode) {
              <div class="form-text">
                Suggested for this name:
                <button type="button" class="btn btn-link btn-sm p-0 align-baseline" (click)="applySuggestedCode()">
                  <code>{{ suggestedCode }}</code>
                </button>
              </div>
            }
          }
          @if (isInvalid('code')) {
            <div class="text-danger small mt-1">
              Use lowercase letters, digits and underscores, starting with a letter.
            </div>
          }
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">Description</label>
          <textarea
            class="form-control"
            formControlName="description"
            rows="2"
            placeholder="What this role is for..."></textarea>
        </div>
      </div>

      <div class="modal-footer border-0">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="roleForm.invalid || isSaving">
          @if (isSaving) {
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Saving...
          } @else {
            {{ isEditMode ? 'Update' : 'Create' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
